/**
 * Utility functions for interacting with Hedera NFTs and tokens
 */

/**
 * Helper function to identify the artist based on NFT serial number
 * - Watusi: serial numbers 1-2
 * - Vaudeville: serial numbers 3-8
 * - litterthanlit: serial number 9
 * - Soluna: same as Watusi (1-2) as they collaborated
 * 
 * @param {number} serialNumber - The serial number of the NFT
 * @returns {number} The artist ID (1: Varda Veil, 2: Soluna, 3: Watusi, 4: litterthanlit)
 */
export const getArtistIdFromSerialNumber = (serialNumber) => {
    // Convert to number to ensure proper comparison
    const serial = Number(serialNumber);
    
    // Watusi and Soluna (collaboration) - serial numbers 1-2
    if (serial >= 1 && serial <= 2) {
        return 3; // Watusi artist_id
    }
    // Vaudeville - serial numbers 3-8
    else if (serial >= 3 && serial <= 8) {
        return 1; // Varda Veil artist_id
    }
    // litterthanlit - serial number 9
    else if (serial === 9) {
        return 4; // litterthanlit artist_id
    }
    // Default case - if we can't identify the artist
    return null;
};

/**
 * Fetches NFTs from a specified Hedera collection
 * @param {string} tokenId - The Hedera token ID for the collection
 * @returns {Promise<Object>} - Object containing the processed NFTs
 */
export const fetchHederaCollection = async (tokenId) => {
    try {
        const baseUrl = 'https://mainnet-public.mirrornode.hedera.com';
        const apiPath = `/api/v1/tokens/${tokenId}/nfts?limit=20`;
        
        console.log('Fetching Hedera collection:', tokenId);
        console.log('Full URL:', `${baseUrl}${apiPath}`);
        
        const response = await fetch(`${baseUrl}${apiPath}`);
        console.log('Response status:', response.status);
        
        if (!response.ok) {
            throw new Error(`Failed to fetch collection: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('Collection data received:', data);
        console.log('NFTs in collection:', data.nfts?.length || 0);
        
        // Get additional metadata for each NFT
        const nftsWithMetadata = [];
        
        for (const nft of data.nfts) {
            try {
                // Fetch additional NFT info including metadata
                const nftInfoResponse = await fetch(`${baseUrl}/api/v1/tokens/${tokenId}/nfts/${nft.serial_number}`);
                if (nftInfoResponse.ok) {
                    const nftInfo = await nftInfoResponse.json();
                    console.log(`NFT ${nft.serial_number} info:`, nftInfo);
                    nftsWithMetadata.push(nftInfo);
                } else {
                    console.error(`Failed to fetch NFT ${nft.serial_number} info:`, nftInfoResponse.status);
                    nftsWithMetadata.push(nft);
                }
            } catch (error) {
                console.error(`Error fetching NFT ${nft.serial_number} info:`, error);
                nftsWithMetadata.push(nft);
            }
        }
        
        // Process NFTs to extract metadata following the guide's recommendations
        const processedNfts = await Promise.all(nftsWithMetadata.map(async (nft) => {
            try {
                // Basic NFT info
                const nftData = {
                    tokenId: nft.token_id,
                    serialNumber: nft.serial_number,
                    name: `NFT #${nft.serial_number}`,
                    imageUrl: '',
                    type: 'image'
                };
                
                // Try to decode metadata if available
                if (nft.metadata) {
                    try {
                        // Decode base64 metadata as per the guide
                        const decodedMetadata = atob(nft.metadata);
                        console.log(`Decoded metadata for NFT ${nft.serial_number}:`, decodedMetadata);
                        
                        // Check for special HCS URLs (Kiloscribe)
                        if (decodedMetadata.startsWith('hcs://')) {
                            // Handle HCS URLs as per the guide
                            const match = decodedMetadata.match(/hcs:\/\/(\d+)\/(\d+\.\d+\.\d+)/);
                            if (match) {
                                const hcsUrl = `https://kiloscribe.com/api/inscription-cdn/${match[2]}`;
                                console.log(`Converting HCS URL to: ${hcsUrl}`);
                                
                                try {
                                    const hcsResponse = await fetch(hcsUrl);
                                    if (hcsResponse.ok) {
                                        const content = await hcsResponse.text();
                                        try {
                                            // Try to parse as JSON
                                            const metadata = JSON.parse(content);
                                            nftData.name = metadata.name || nftData.name;
                                            nftData.description = metadata.description || '';
                                            
                                            if (metadata.image) {
                                                if (metadata.image.startsWith('hcs://')) {
                                                    // Handle nested HCS image URL
                                                    const imageMatch = metadata.image.match(/hcs:\/\/(\d+)\/(\d+\.\d+\.\d+)/);
                                                    if (imageMatch) {
                                                        nftData.imageUrl = `https://kiloscribe.com/api/inscription-cdn/${imageMatch[2]}`;
                                                    }
                                                } else {
                                                    nftData.imageUrl = metadata.image;
                                                }
                                            }
                                        } catch (e) {
                                            // If not JSON, use content as SVG
                                            nftData.imageUrl = hcsUrl;
                                            nftData.type = 'svg';
                                        }
                                    }
                                } catch (e) {
                                    console.error('Error fetching HCS content:', e);
                                }
                            }
                        } else {
                            // Try to parse as JSON
                            try {
                                const metadata = JSON.parse(decodedMetadata);
                                nftData.name = metadata.name || nftData.name;
                                nftData.description = metadata.description || '';
                                
                                if (metadata.image) {
                                    nftData.imageUrl = metadata.image;
                                    
                                    // Detect NFT type
                                    if (metadata.image.endsWith('.mp4') || metadata.image.endsWith('.webm') || metadata.format === 'video') {
                                        nftData.type = 'video';
                                    } else if (metadata.image.endsWith('.glb') || metadata.image.endsWith('.gltf') || metadata.format === 'glb' || metadata.format === 'gltf') {
                                        nftData.type = 'model';
                                    } else if (metadata.image.endsWith('.svg') || metadata.format === 'svg') {
                                        nftData.type = 'svg';
                                    }
                                }
                                
                                // Convert IPFS URLs to gateway URLs
                                if (nftData.imageUrl && nftData.imageUrl.startsWith('ipfs://')) {
                                    nftData.imageUrl = nftData.imageUrl.replace('ipfs://', 'https://ipfs.io/ipfs/');
                                }
                            } catch (e) {
                                // If not JSON, use as URL if it looks like a URL
                                console.log('Metadata is not valid JSON:', decodedMetadata);
                                if (decodedMetadata.startsWith('http')) {
                                    nftData.imageUrl = decodedMetadata;
                                }
                            }
                        }
                    } catch (e) {
                        console.error('Error decoding metadata:', e);
                    }
                }
                
                console.log(`Processed NFT ${nft.serial_number}:`, nftData);
                return nftData;
            } catch (error) {
                console.error(`Error processing NFT ${nft.token_id}:${nft.serial_number}:`, error);
                return {
                    tokenId: nft.token_id,
                    serialNumber: nft.serial_number,
                    name: `NFT #${nft.serial_number}`,
                    error: true
                };
            }
        }));
        
        return {
            nfts: processedNfts,
            nextLink: data.links?.next || null
        };
    } catch (error) {
        console.error('Error fetching Hedera collection:', error);
        throw error;
    }
};

/**
 * Gets the balance of a specific token for an account
 * @param {string} accountId - The Hedera account ID
 * @param {string} tokenId - The Hedera token ID
 * @returns {Promise<number>} - The token balance
 */
export const getTokenBalance = async (accountId, tokenId) => {
    try {
        const baseUrl = 'https://mainnet-public.mirrornode.hedera.com';
        const apiPath = `/api/v1/accounts/${accountId}/tokens?token.id=${tokenId}`;
        
        const response = await fetch(`${baseUrl}${apiPath}`);
        
        if (!response.ok) {
            throw new Error(`Failed to fetch token balance: ${response.status}`);
        }
        
        const data = await response.json();
        
        if (data.tokens && data.tokens.length > 0) {
            return parseInt(data.tokens[0].balance);
        }
        
        return 0;
    } catch (error) {
        console.error('Error fetching token balance:', error);
        return 0;
    }
};
